
import { useEffect, useState } from "react";
import { Box, Heading, Text, Spinner } from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

const BLOG_ID = "460807346859212205";

// Configure this in the frontend project's .env file.
const API_KEY = process.env.REACT_APP_BLOGGER_API_KEY;

function slugify(text) {
  return (text || "") 
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function cleanArticleHtml(html) {
  // Basic cleanup of untrusted Blogger HTML.
  // For production, use a proper HTML sanitizer as well.
  const doc = new DOMParser().parseFromString(
    html || "",
    "text/html"
  );

  doc.querySelectorAll(
    "script, style, iframe, object, embed, form"
  ).forEach((element) => element.remove());

  doc.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();

      if (
        name.startsWith("on") ||
        ((name === "href" || name === "src") &&
          value.startsWith("javascript:"))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return doc.body.innerHTML;
}

export default function NewsPage() {
  const { newsSlug } = useParams();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadPost() {
      setLoading(true);
      setPost(null);
      setError("");

      try {
        if (!API_KEY) {
          throw new Error(
            "The Blogger API key is not configured."
          );
        }

        let pageToken = null;
        let foundPost = null;
        let pageCount = 0;

        // Search Blogger's paginated post list.
        do {
          const params = new URLSearchParams({
            key: API_KEY,
            fetchImages: "true",
            maxResults: "100",
          });

          if (pageToken) {
            params.set("pageToken", pageToken);
          }

          const response = await fetch(
            `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID}/posts?${params}`
          );

          if (!response.ok) {
            throw new Error(
              "Could not load articles from Blogger."
            );
          }

          const data = await response.json();

          foundPost = (data.items || []).find(
            (item) => slugify(item.title) === newsSlug
          );

          if (foundPost) {
            break;
          }

          pageToken = data.nextPageToken || null;
          pageCount++;
        } while (pageToken && pageCount < 10);

        if (!foundPost) {
          throw new Error("News article not found.");
        }

        if (!cancelled) {
          setPost(foundPost);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.message || "Unable to load this article."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPost();

    return () => {
      cancelled = true;
    };
  }, [newsSlug]);

  if (loading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" />
        <Text mt={4}>Loading news article...</Text>
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box textAlign="center" py={12} px={4}>
        <Heading size="lg">
          News article not found
        </Heading>

        <Text mt={3}>{error}</Text>

        <Text mt={4}>
          <Link to="/">Back to home</Link>
        </Text>
      </Box>
    );
  }

  const description = (post.content || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 220);

  const image =
    post.images?.[0]?.url ||
    post.content?.match(/<img[^>]+src=["']([^"']+)["']/i)?.[1] ||
    "";

  const articleHtml = cleanArticleHtml(post.content || "");

  const canonicalUrl =
    `https://leaderbored.gg/news/${newsSlug}`;

  return (
    <Box maxW="900px" mx="auto" px={4} py={8}>
      <Helmet>
        <title>{post.title} | Leaderbored</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonicalUrl}
        />

        <meta property="og:title" content={post.title} />
        <meta
          property="og:description"
          content={description}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />

        {image && (
          <meta property="og:image" content={image} />
        )}
      </Helmet>

      <Heading mb={6}>{post.title}</Heading>

      {image && (
        <Box
          as="img"
          src={image}
          alt={post.title}
          maxW="100%"
          borderRadius="lg"
          mb={6}
        />
      )}

      <Box
        className="blogger-article-content"
        dangerouslySetInnerHTML={{ __html: articleHtml }}
      />

      <Text mt={8}>
        <Link to="/">Back to home</Link>
      </Text>
    </Box>
  );
}
